import { categoryMeta } from "./mana";
import type { DeckCategoryId, RecommendationReason } from "./types";
import { titleCase } from "./utils";

const mechanicEventLabels: Readonly<Record<string, string>> = {
  "permanent.tap": "Tap",
  "permanent.untap": "Untap",
  "permanent.sacrifice": "Sacrifice",
  "permanent.create_token": "Create tokens",
  "permanent.put_counter": "Counters",
  "zone.draw_card": "Draw cards",
  "zone.enter_battlefield": "Enter-the-battlefield",
  "combat.attack": "Attack",
};

/** Human label for a mechanic preference or candidate group. */
export function describeMechanicGroup(group: string): string {
  const separator = group.indexOf(":");
  if (separator < 0) return titleCase(group.replace(/[._]/g, " "));
  const direction = group.slice(0, separator);
  const eventId = group.slice(separator + 1);
  const event = mechanicEventLabels[eventId] ?? titleCase(eventId.replace(/[._]/g, " "));
  return `${event} ${direction === "produces" ? "enablers" : "payoffs"}`;
}

/** Short human sentence for a structured recommendation reason. */
export function describeReason(
  reason: RecommendationReason,
  nameOf: (oracleId: string) => string,
): string {
  const field = (key: string): string => String(reason[key] ?? "");
  switch (reason.kind) {
    case "locked":
      return "Locked by you";
    case "commander": {
      const theme = field("theme");
      const commander = nameOf(field("commanderId"));
      if (reason.code === "COMMANDER_ENABLER") return `Enables ${commander}`;
      if (reason.code === "COMMANDER_COST_REDUCER") return `Makes ${commander} cheaper`;
      return theme ? `Shares the ${theme} theme with ${commander}` : `Synergy with ${commander}`;
    }
    case "role": {
      const role = field("roleId");
      const label = categoryMeta[role as DeckCategoryId]?.label ?? titleCase(role);
      return `Fills ${label.toLowerCase()} (rank #${field("rank")})`;
    }
    case "pair":
      return reason["polarity"] === "negative"
        ? `Conflicts with ${nameOf(field("partnerId"))}`
        : `Pairs with ${nameOf(field("partnerId"))} (${titleCase(reason.code.toLowerCase())})`;
    case "package":
      return reason.code === "PACKAGE_ACTIVE"
        ? `Completes package ${titleCase(field("packageId"))}`
        : `Part of incomplete package ${titleCase(field("packageId"))}`;
    case "curve":
      return `Mana value ${field("manaValue")}: ${field("actualCount")} of ${field("targetCount")} target`;
    case "source":
      if (field("group").startsWith("combo-support:")) {
        const [, planId, role] = field("group").split(":");
        return `Combo plan support (${titleCase(role ?? "card")}): ${titleCase((planId ?? "").replaceAll("-", " "))}`;
      }
      return field("source") === "mechanic"
        ? `Mechanic: ${describeMechanicGroup(field("group"))}`
        : `${titleCase(field("source"))}: ${titleCase(field("group"))}`;
    default:
      return titleCase(reason.code.toLowerCase());
  }
}

export function formatScore(value: number): string {
  const scaled = value / 1000;
  return `${scaled > 0 ? "+" : ""}${scaled.toFixed(Math.abs(scaled) >= 10 ? 1 : 2)}`;
}
